import React, { ComponentProps, useCallback } from 'react';

import PropTypes from 'prop-types';

import { BasicButton } from '@adsk/alloy-react-button';
import { ChevronDownIcon } from '@adsk/alloy-react-icon';
import theme, { stylePropType } from '@adsk/alloy-react-theme';

type ExpandButtonProps = ComponentProps<typeof BasicButton> & {
  onExpand: (p: { isExpanded: boolean; recurse: boolean }) => void;
  isExpanded?: boolean;
};
const ExpandButton = React.forwardRef<HTMLButtonElement, ExpandButtonProps>(
  ({ style, onExpand, isExpanded = false, ...props }, ref) => {
    const handleExpand = useCallback<
      React.MouseEventHandler<HTMLButtonElement>
    >(
      (e) => {
        e.stopPropagation();
        onExpand({ isExpanded: !isExpanded, recurse: e.shiftKey });
      },
      [onExpand, isExpanded],
    );

    return (
      <BasicButton
        ref={ref}
        data-testid="ExpandIcon"
        aria-label={'expand'}
        tabIndex={-1}
        style={{
          color: theme.tokens.colors.icon.secondary.value,
          ...(style as object),
          padding: 0,
        }}
        onClick={handleExpand}
        onBlur={(e) => {
          // Don't trigger parent's onBlur or we'll unhighlight the node
          e.stopPropagation();
        }}
        {...props}
      >
        <ChevronDownIcon
          style={{
            display: 'block',
            transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.15s linear',
          }}
        />
      </BasicButton>
    );
  },
);

ExpandButton.displayName = 'ExpandButton';

ExpandButton.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Expansion state */
  isExpanded: PropTypes.bool,
  /** Callback fired on click */
  onExpand: PropTypes.func.isRequired,
};

export default ExpandButton;
